/**
 * Payment Service
 * 
 * Service for handling payment-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface HistoricalTransactionFilters {
  fromDate?: string;
  toDate?: string;
  paymentId?: string;
  orderId?: string; // Search by order_id in payementData.transaction_details.order_id
  idClient?: string; // Filter by paymentData.customer_details.id_client
  province?: string; // Filter by province
  city?: string; // Filter by city (kabupaten/kota)
  cursor?: string;
  limit?: number;
  direction?: "next" | "previous";
  sortBy?: "createdAt" | "status" | "province" | "kabkot" | "_id";
  sortOrder?: "asc" | "desc";
}

export interface HistoricalTransaction {
  _id: string;
  status: string;
  paymentId?: string;
  province?: string;
  kabkot?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    _id: string;
    callsign: string;
    nama_lengkap: string;
    email: string;
  };
  payementData?: any;
  paymentGatewayData?: any;
  midtransStatus?: {
    transaction_status?: string;
    status_code?: string;
    status_message?: string;
    transaction_id?: string;
    order_id?: string;
    gross_amount?: string;
    payment_type?: string;
    settlement_time?: string;
    fraud_status?: string;
    [key: string]: any;
  };
}

export interface HistoricalTransactionResponse {
  data: HistoricalTransaction[];
  nextCursor: string | null;
  previousCursor: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
  total?: number;
}

class PaymentService {
  /**
   * Check if a settlement payment exists for a user
   * @param idClient - Client ID
   * @param callsign - User's callsign
   * @returns Promise<boolean> - true if settlement exists, false otherwise
   */
  async checkSettlement(idClient: string, callsign: string): Promise<{ hasSettlement: boolean; masa_laku_kta: string | null }> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/settlement/check?idClient=${encodeURIComponent(idClient)}&callsign=${encodeURIComponent(callsign)}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { hasSettlement: data.hasSettlement || false, masa_laku_kta: data.masa_laku_kta || null };
    } catch (error) {
      console.error("Error checking settlement status:", error);
      return { hasSettlement: false, masa_laku_kta: null };
    }
  }

  /**
   * Fetch historical transactions with filters
   * @param filters - Filter options (fromDate, toDate, paymentId, cursor, limit, direction)
   * @returns Promise with paginated transaction data
   */
  async fetchHistoricalTransactions(
    filters: HistoricalTransactionFilters = {}
  ): Promise<HistoricalTransactionResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.fromDate) {
        params.append("fromDate", filters.fromDate);
      }
      
      if (filters.toDate) {
        params.append("toDate", filters.toDate);
      }
      
      if (filters.paymentId) {
        params.append("paymentId", filters.paymentId);
      }
      
      if (filters.orderId) {
        params.append("orderId", filters.orderId);
      }
      
      if (filters.idClient) {
        params.append("idClient", filters.idClient);
      }
      
      if (filters.province) {
        params.append("province", filters.province);
      }
      
      if (filters.city) {
        params.append("kabkot", filters.city);
      }
      
      if (filters.cursor) {
        params.append("cursor", filters.cursor);
      }
      
      if (filters.limit) {
        params.append("limit", filters.limit.toString());
      }
      
      if (filters.direction) {
        params.append("direction", filters.direction);
      }
      
      if (filters.sortBy) {
        params.append("sortBy", filters.sortBy);
      }
      
      if (filters.sortOrder) {
        params.append("sortOrder", filters.sortOrder);
      }

      // Get auth token from cookie if available (for browser) or from localStorage (for API clients)
      const getAuthToken = (): string | null => {
        // Try to get from localStorage first (for API clients)
        if (typeof window !== "undefined") {
          const token = localStorage.getItem("auth_token");
          if (token) return token;
        }
        return null;
      };

      const authToken = getAuthToken();
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      // Add Authorization header if token is available
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transactions?${params.toString()}`,
        {
          method: "GET",
          credentials: "include", // Also send cookies for browser-based auth
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data as HistoricalTransactionResponse;
    } catch (error) {
      console.error("Error fetching historical transactions:", error);
      throw error;
    }
  }

  /**
   * Trigger Midtrans refetch for a date range
   * @param from - Start date (YYYY-MM-DD format)
   * @param to - End date (YYYY-MM-DD format)
   * @param limit - Maximum records to process (default: 100000)
   * @returns Promise with refetch result and summary
   */
  async triggerMidtransRefetch(
    from: string,
    to: string,
    limit: number = 100000
  ): Promise<{ success: boolean; message: string; summary: any }> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/midtrans-refetch`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ from, to, limit }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error triggering Midtrans refetch:", error);
      throw error;
    }
  }
}

export const paymentService = new PaymentService();

